import React, { useCallback, useEffect, useState } from 'react';
import InfoCard from './InfoCard';
import apiClient from '../../config/apiClient';
import Loading from '../../components/Loading';
import { CalendarToday, Refresh } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNotifications } from '../../contexts/NotificationsContext';

interface ReportData {
  reportId: number;
  tenantName: string;
  reportDate: string;
  reportStatus: string;
  SharedMailbox: number;
  UserMailbox: number;
  RoomMailbox: number;
  ServiceMailbox: number;
  OutOfScope: number;
  ADMAccount: number;
  scopeChangeStatus: string;
  changePercentage: number;
  total: number;
  threshold: number;
}

interface DashboardReportProps {
  tenantId: string | null;
  refreshSignal: boolean;
  handleRefresh: () => void;
}

const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return 'No Date Available';
  const date = new Date(dateString);
  return `${date.toLocaleDateString('en-GB')} ${date.toLocaleTimeString('en-GB')}`;
};

const DashboardReport: React.FC<DashboardReportProps> = ({ tenantId, refreshSignal, handleRefresh }) => {
  const [data, setData] = useState<ReportData | null>(null);
  const [reportDate, setReportDate] = useState<string | null>(null);
  const [loadingReport, setLoadingReport] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { notifications } = useNotifications();

  const fetchReport = useCallback(async () => {
    if (tenantId) {
      setLoadingReport(true);
      try {
        const response = await apiClient.get(`/report/${tenantId}`);
        const data = response.data;
  
        if (data && data.reportDate) {
          setData(data);
          setReportDate(data?.reportDate || null);
          setError(null);
        } else {
          setData(null);
          setReportDate(null);
          setError('No report data available for this tenant.');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setData(null);
        setReportDate(null);
        setError('Error fetching report data.');
      } finally {
        setLoadingReport(false);
      }
    }
  }, [tenantId]);
  
  useEffect(() => {
    fetchReport();
  }, [fetchReport, tenantId, refreshSignal]);

  useEffect(() => {
    notifications.forEach((notif: any) => {
      if (notif.id === tenantId && notif.status === 'generated') {
        toast.success(`Report generated for ${data?.tenantName}. Check the information`);
        fetchReport();
      }
    });
  }, [notifications, fetchReport, data?.tenantName, tenantId]);

  const statusColors: Record<string, string> = {
    Completed: 'text-green-600',
    'In Progress': 'text-yellow-600',
    Failed: 'text-red-700',
  };
  const status = data?.reportStatus ?? 'Unknown';

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">Last Report Tenant: {data?.tenantName || 'N/A'}</h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <CalendarToday className="text-gray-500" />
            <p className="text-gray-700">{formatDateTime(reportDate)}</p>
          </div>
          <p className={`font-bold ${statusColors[status] || 'text-gray-600'}`}>
            Status: {data?.reportStatus || 'Unknown'}
          </p>
          <button
            className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-800 transition duration-300"
            onClick={handleRefresh}
          >
            <Refresh fontSize="small" />
          </button>
        </div>
      </div>

      {loadingReport ? (
        <Loading />
      ) : error ? (
        <p className="text-center text-red-800">{error}</p>
      ) : data ? (
        <div className="grid grid-cols-4 gap-4 justify-center">
          <InfoCard classification="SharedMailbox" count={data.SharedMailbox} />
          <InfoCard classification="UserMailbox" count={data.UserMailbox} />
          <InfoCard classification="RoomMailbox" count={data.RoomMailbox} />
          <InfoCard classification="ServiceMailbox" count={data.ServiceMailbox} />
          <InfoCard classification="OutOfScope" count={data.OutOfScope} />
          <InfoCard classification="ADMAccount" count={data.ADMAccount} />
          <InfoCard classification="scopeChangeStatus" count={data.scopeChangeStatus} />
          <InfoCard classification="changePercentage" count={data.changePercentage} />
          <InfoCard classification="Threshold" count={data.threshold} />
          <InfoCard classification="total" count={data.total} />
        </div>
      ) : (
        <p className="text-center">Please select a tenant to view the report...</p>
      )}
    </div>
  );
};

export default DashboardReport;
